import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, GitBranch, Bot, Receipt, BarChart3, Network as NetworkIcon,
  ArrowRight, Check, Sparkles, Zap, Clock, DollarSign, FileWarning, Plug,
} from 'lucide-react';

const ROI = [
  { icon: Zap, value: '78%', label: 'of checks auto-cleared', sub: 'no staff time' },
  { icon: Clock, value: '~12 hrs', label: 'saved per week', sub: 'on manual verification' },
  { icon: FileWarning, value: 'Caught', label: 'before the visit', sub: 'referrals, auth, inactive plans' },
  { icon: DollarSign, value: '$12k+', label: 'flagged to collect', sub: 'upfront, every week' },
];

const STEPS = [
  { n: 1, icon: ShieldCheck, t: 'Verify', d: 'Eligibility runs the moment an appointment is booked — no daily polling, no per-patient busywork.' },
  { n: 2, icon: Zap, d: 'Clean, in-network, deductible-met visits clear themselves. Your team never sees them.', t: 'Auto-clear' },
  { n: 3, icon: GitBranch, t: 'Act', d: 'Only the exceptions reach the front desk — each with a plain-English reason and the next action ready.' },
];

const AGENTS = [
  { icon: ShieldCheck, t: 'Verification & Risk', d: 'Runs 270/271, parses the response, scores each visit, and routes the exceptions.' },
  { icon: GitBranch, t: 'Referral & Authorization', d: 'Catches gatekeeper plans and prior-auth needs before they turn into denials.' },
  { icon: Bot, t: 'Patient Communication', d: 'Drafts the message for inactive coverage or estimates — you approve, it sends, eligibility re-runs.' },
  { icon: NetworkIcon, t: 'Data Sync', d: 'Reconciles your records against the payer response and writes corrections back to Athena.' },
  { icon: Receipt, t: 'Estimator', d: 'Good-faith estimates from your contracts + history, so you collect the right amount at check-in.' },
  { icon: BarChart3, t: 'Retrospective', d: 'Turns recurring denials into policies that prevent the next one.' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-16 px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand-600 text-white flex items-center justify-center font-bold">D</div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900">Due Health</div>
              <div className="text-[10px] uppercase tracking-wider text-brand-500 font-medium">Eligibility AI</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#roi" className="hover:text-slate-900">Results</a>
            <a href="#agents" className="hover:text-slate-900">The agents</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost">Sign in</Button></Link>
            <Link to="/login"><Button className="bg-brand-600 hover:bg-brand-700 text-white">Book a demo</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="dh-hero">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white border border-brand-200 text-brand-700 text-xs font-medium px-3 py-1 rounded-full">
              <Plug className="h-3.5 w-3.5" />Works inside Athena — or standalone
            </div>
            <h1 className="mt-6 text-5xl md:text-6xl font-semibold tracking-tight text-slate-900">
              Your front desk, <span className="text-brand-600">on autopilot.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl">
              Due Health verifies insurance for every appointment before the visit, clears the clean ones automatically, and hands your team only the exceptions — each with the reason and the next step ready. Fewer denials, more collected upfront, hours back every week.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/login"><Button size="lg" className="bg-brand-600 hover:bg-brand-700 text-white">Book a demo <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline">Open the live demo</Button></Link>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>HIPAA-eligible</span><span>·</span><span>Stedi clearinghouse</span><span>·</span><span>Athena-native</span><span>·</span><span>Live in a day</span>
            </div>
          </div>

          {/* Product preview */}
          <div className="mt-14 rounded-2xl border border-slate-200 bg-white shadow-xl shadow-brand-100/40 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="text-xs text-slate-400 ml-2 mono">app.duehealth.com / worklist</span>
            </div>
            <div className="grid md:grid-cols-4 gap-0">
              {[
                { label: "Today's appointments", value: '210', sub: 'verified by agents', tone: 'text-slate-900' },
                { label: 'Auto-cleared', value: '164', sub: 'no human needed', tone: 'text-emerald-600' },
                { label: 'Need attention', value: '46', sub: 'routed with a next step', tone: 'text-amber-600' },
                { label: 'Flagged to collect', value: '$12.4k', sub: 'upfront, this week', tone: 'text-brand-600' },
              ].map((s) => (
                <div key={s.label} className="p-6 border-r last:border-r-0 border-slate-100">
                  <div className="text-xs text-slate-500">{s.label}</div>
                  <div className={`mt-2 text-3xl font-semibold tnum ${s.tone}`}>{s.value}</div>
                  <div className="mt-1 text-xs text-slate-500">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">The problem</div>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">Your staff checks ~200 patients a day, by hand.</h2>
            <p className="mt-3 text-slate-600">Every appointment means logging into a portal, reading a scattered insurance response, and deciding what to do. Miss a referral, a prior auth, or an inactive plan and the claim gets denied — after you've already seen the patient.</p>
          </div>
          <div className="dh-card p-6">
            <div className="text-sm font-semibold text-slate-900 mb-3">What slips through today</div>
            <ul className="space-y-2.5 text-sm text-slate-700">
              {['Referrals not on file for gatekeeper plans', 'Prior auth missed on procedures', 'Coverage inactive — found at check-in', 'High deductibles not collected upfront'].map((t) => (
                <li key={t} className="flex items-start gap-2"><FileWarning className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">How it works</div>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">Three steps. Your team only does the third.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="dh-card p-6">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                    <span className="text-xs font-semibold text-slate-400 tnum">STEP {s.n}</span>
                  </div>
                  <div className="mt-4 font-semibold text-slate-900">{s.t}</div>
                  <div className="mt-1 text-sm text-slate-600">{s.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section id="roi" className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">Results</div>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">Less manual work. Fewer denials. More collected.</h2>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ROI.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.label} className="dh-card p-5">
                <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><Icon className="h-4 w-4" /></div>
                <div className="mt-3 text-2xl font-semibold text-slate-900 tnum">{r.value}</div>
                <div className="text-sm text-slate-700">{r.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.sub}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-slate-400">Representative figures from a 3-provider dermatology practice. Your numbers depend on payer mix and volume.</div>
      </section>

      {/* Agents */}
      <section id="agents" className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">The agents</div>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">A team that handles the pre-visit busywork</h2>
            <p className="mt-3 text-slate-600">Each agent owns one job, shows its reasoning with a confidence score, and asks for a human only when it should. You set what runs on autopilot and what waits for approval.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {AGENTS.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.t} className="dh-card p-6 lift">
                  <div className="h-10 w-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                  <div className="mt-4 font-semibold text-slate-900">{a.t}</div>
                  <div className="mt-1 text-sm text-slate-600">{a.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust / integration */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Plug, t: 'Athena-native', d: 'Runs embedded inside Athena via its APIs, or as a standalone dashboard. One-click writeback of corrected demographics.' },
            { icon: ShieldCheck, t: 'HIPAA-eligible', d: 'Processes patient data only for scheduled appointments. Full agent audit trail; PHI kept separate from analytics.' },
            { icon: Bot, t: 'Confidence-scored AI', d: 'Every agent output carries a confidence score and links back to the source 271 — no black boxes.' },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.t} className="dh-card p-6"><div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon className="h-4 w-4" /></div><div className="mt-3 font-semibold text-slate-900">{s.t}</div><div className="mt-1 text-sm text-slate-600">{s.d}</div></div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-xs uppercase tracking-wider text-brand-600 font-semibold">FAQ</div>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-slate-900">Common questions</h2>
          <div className="mt-8 space-y-3">
            {[
              { q: 'How fast can we go live?', a: 'Most practices run their first checks on day one. Connect Athena, point us at your schedule, and the agents start verifying.' },
              { q: 'Do the agents act without us?', a: 'Only where you allow it. Each policy is Off, Warn, or Enforce; agents run on Autopilot or Copilot (draft-and-approve). You set the line.' },
              { q: 'Will it create more work?', a: "The opposite. Clean visits auto-clear and never reach staff. Your team sees only the exceptions, with the next action already prepared." },
              { q: 'Is our patient data safe?', a: 'Yes. HIPAA-eligible, BAAs with Stedi and the backend, PHI encrypted and kept separate from analytics, and a full audit trail of every agent action.' },
            ].map((f) => (
              <details key={f.q} className="dh-card p-5 group">
                <summary className="flex items-center justify-between cursor-pointer list-none"><span className="font-medium text-slate-900">{f.q}</span><Check className="h-4 w-4 text-brand-600" /></summary>
                <p className="mt-3 text-sm text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-slate-900 text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-semibold">See your own schedule, triaged.</h3>
            <p className="mt-2 text-slate-300">Open the live demo — <span className="mono">sarah@pacificderm.com</span> / <span className="mono">demo1234</span>.</p>
          </div>
          <Link to="/login"><Button size="lg" className="bg-brand-500 hover:bg-brand-400 text-white">Open the demo <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div>© 2026 Due HealthTech · All rights reserved</div>
          <div className="flex gap-5"><a href="#" className="hover:text-slate-900">Privacy</a><a href="#" className="hover:text-slate-900">Terms</a><a href="#" className="hover:text-slate-900">Security</a></div>
        </div>
      </footer>
    </div>
  );
}
